import { useCallback } from 'react';

/**
 * Keyboard nav helper for modal forms (deposit / coin-cut / mark-paid etc.).
 *
 * On the modal's container element, spread the returned `containerProps`.
 * Behaviour when focus is inside an <input>, <textarea>, or <select>:
 *  - ArrowDown  → focus next form control in the container
 *  - ArrowUp    → focus previous form control
 *  - Enter      → call `onSubmit` (only for <input>, not <textarea>; Shift+Enter
 *                 is left alone so multi-line textareas still work).
 *
 * The hook ignores disabled controls and elements hidden via `type=hidden`.
 */
export function useArrowKeyFormNav(onSubmit) {
  const onKeyDown = useCallback((e) => {
    const tag = (e.target?.tagName || '').toUpperCase();
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;

    const container = e.currentTarget;
    const focusables = Array.from(
      container.querySelectorAll('input, textarea, select')
    ).filter((el) => !el.disabled && el.type !== 'hidden' && el.tabIndex !== -1);
    if (focusables.length === 0) return;
    const idx = focusables.indexOf(e.target);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = focusables[(idx + 1 + focusables.length) % focusables.length];
      next?.focus();
      if (next?.select) try { next.select(); } catch (_) {}
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = focusables[(idx - 1 + focusables.length) % focusables.length];
      prev?.focus();
      if (prev?.select) try { prev.select(); } catch (_) {}
    } else if (e.key === 'Enter' && !e.shiftKey && tag === 'INPUT') {
      e.preventDefault();
      if (typeof onSubmit === 'function') onSubmit();
    }
  }, [onSubmit]);

  return { onKeyDown };
}

export default useArrowKeyFormNav;
